/**
 * "Agenda da clínica" (ADR-0028): a Cliqfy escolhe, para cada parte da tela da
 * recepção, a operação SQL que a atende. É o que deixa o fluxo do CRM igual
 * para qualquer sistema de clínica: trocar o SQL não muda o CRM.
 *
 * O servidor confere cada operação antes de gravar (tipo, publicada, número de
 * parâmetros, uma conexão só) e devolve o problema por parte; nada é gravado
 * enquanto houver um.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ErroDaApi, type AgendaDaClinica as Mapa, type OperacaoDaApi } from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import { useAgendaDaClinica, useApi, useTodasAsOperacoesDeDados } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { useSessao } from '@/shared/sessao/contexto';
import { Botao, CampoSelecao, Selo } from '@/shared/ui';
import { Carregando } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  PARTES_DA_AGENDA,
  mapaParaGravar,
  mudou,
  operacoesDaParte,
  problemasDoMapa,
} from '../lib/agenda-da-clinica';
import './agenda-da-clinica.css';

export function AgendaDaClinica(): ReactNode {
  const mapa = useAgendaDaClinica(true);
  const operacoes = useTodasAsOperacoesDeDados(true);
  return (
    <section className="agenda-da-clinica" aria-labelledby="agenda-da-clinica-titulo">
      <div className="agenda-da-clinica__cabeca">
        <h2 id="agenda-da-clinica-titulo" className="agenda-da-clinica__titulo">
          Agenda da clínica
        </h2>
        <p className="agenda-da-clinica__apoio">
          Qual operação atende cada parte da tela da recepção: buscar paciente, vagas, agendar.
          Todas usam a mesma conexão com a clínica.
        </p>
      </div>
      {mapa.isPending || operacoes.isPending ? (
        <Carregando forma="lista" linhas={4} rotulo="Lendo a agenda da clínica…" />
      ) : mapa.isError || operacoes.isError ? (
        <Faixa tom="bloqueado">
          {recadoDeErro(mapa.error ?? operacoes.error, {
            generica: 'Não foi possível ler a agenda da clínica.',
          })}
        </Faixa>
      ) : (
        <Formulario gravado={mapa.data} operacoes={operacoes.data.itens} />
      )}
    </section>
  );
}

function Formulario({
  gravado,
  operacoes,
}: {
  gravado: Mapa;
  operacoes: readonly OperacaoDaApi[];
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const { recarregarMe } = useSessao();
  const [rascunho, definirRascunho] = useState<Record<string, string>>({ ...gravado.operacoes });
  /** O mapa gravado de quando a edição começou: o rascunho é comparado com ele. */
  const [base, definirBase] = useState<Record<string, string>>({ ...gravado.operacoes });
  /** A versão da base: vai no salvar, e o servidor recusa se outra pessoa salvou no meio. */
  const [versaoDaBase, definirVersaoDaBase] = useState(gravado.versao);
  const [recado, definirRecado] = useState<string | null>(null);
  /*
   * Outra pessoa da Cliqfy salvou enquanto a tela estava aberta. Sem edição
   * em curso, a tela passa a mostrar o novo; com edição, avisa e não deixa
   * salvar por cima, que desfaria a mudança dela sem ninguém ver.
   */
  const outraVersao = mudou(base, gravado.operacoes);
  const editando = mudou(rascunho, base);
  useEffect(() => {
    if (outraVersao && !editando) {
      definirRascunho({ ...gravado.operacoes });
      definirBase({ ...gravado.operacoes });
      definirVersaoDaBase(gravado.versao);
    }
  }, [outraVersao, editando, gravado.operacoes, gravado.versao]);
  const usarVersaoSalva = (): void => {
    definirRascunho({ ...gravado.operacoes });
    definirBase({ ...gravado.operacoes });
    definirVersaoDaBase(gravado.versao);
    gravar.reset();
  };

  const gravar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      return api.gravarAgendaDaClinica({
        corpo: {
          operacoes: mapaParaGravar(rascunho),
          ...(versaoDaBase ? { versao: versaoDaBase } : {}),
        },
      });
    },
    onMutate: () => definirRecado(null),
    onError: (erro) => {
      // Outra pessoa salvou no meio: relê o mapa, e a faixa do conflito aparece.
      if (outraPessoaSalvou(erro)) {
        void cache.invalidateQueries({ queryKey: chaves.agendaDaClinica() });
      }
    },
    onSuccess: async (salvo) => {
      cache.setQueryData(chaves.agendaDaClinica(), salvo);
      definirRascunho({ ...salvo.operacoes });
      definirBase({ ...salvo.operacoes });
      definirVersaoDaBase(salvo.versao);
      // A recepção recebe o mapa pelo /v1/me: sem recarregar, a tela dela ficaria velha.
      await recarregarMe();
      definirRecado('Agenda da clínica salva. A recepção já usa as operações novas.');
    },
  });

  const problemas = gravar.isError ? problemasDoMapa(gravar.error) : null;
  const temPorParte = problemas && Object.keys(problemas.porParte).length > 0;
  // A recusa do servidor também é conflito: a releitura do mapa pode ainda estar a caminho.
  const conflito = (outraVersao && editando) || outraPessoaSalvou(gravar.error);
  const alterado = !conflito && mudou(rascunho, gravado.operacoes);

  return (
    <form
      className="agenda-da-clinica__formulario"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (alterado && !gravar.isPending) gravar.mutate();
      }}
    >
      <ul className="agenda-da-clinica__partes">
        {PARTES_DA_AGENDA.map((parte) => {
          const opcoes = operacoesDaParte(parte, operacoes, rascunho[parte.parte]);
          const id = `agenda-da-clinica-${parte.parte}`;
          return (
            <li key={parte.parte} className="agenda-da-clinica__parte">
              <div className="agenda-da-clinica__sobre">
                <span className="agenda-da-clinica__nome">
                  {parte.nome}
                  <Selo tom={parte.grava ? 'atencao' : 'neutro'}>
                    {parte.grava ? 'Grava' : 'Lê'}
                  </Selo>
                </span>
                <span className="agenda-da-clinica__ajuda">
                  {parte.ajuda} {recebe(parte.parametros)}
                </span>
              </div>
              <CampoSelecao
                id={id}
                rotuloOculto={`Operação de ${parte.nome}`}
                value={rascunho[parte.parte] ?? ''}
                erro={problemas?.porParte[parte.parte]}
                onChange={(e) => {
                  const valor = e.currentTarget.value;
                  definirRascunho((atual) => ({ ...atual, [parte.parte]: valor }));
                }}
              >
                <option value="">Não ligada</option>
                {opcoes.map((op) => (
                  <option key={op.id} value={op.id}>
                    {op.situacao === 'publicada' ? op.nome : `${op.nome} (não publicada)`}
                  </option>
                ))}
              </CampoSelecao>
            </li>
          );
        })}
      </ul>

      {conflito && (
        <Faixa tom="atencao">
          Outra pessoa salvou a agenda enquanto você editava. Para não desfazer a mudança dela, use
          a versão salva e refaça a sua.{' '}
          <Botao variante="secundario" miudo onClick={usarVersaoSalva}>
            Usar a versão salva
          </Botao>
        </Faixa>
      )}

      {gravar.isError && !outraPessoaSalvou(gravar.error) && (
        <Faixa tom="bloqueado">
          {temPorParte
            ? `Nada foi gravado. ${[...(problemas?.geral ?? []), 'Corrija as partes marcadas.'].join(' ')}`
            : recadoDeErro(gravar.error, {
                generica: 'Não foi possível gravar a agenda da clínica.',
              })}
        </Faixa>
      )}

      <div className="agenda-da-clinica__rodape">
        <p className="agenda-da-clinica__recado" role="status" aria-live="polite">
          {recado ?? ''}
        </p>
        <Botao
          variante="primario"
          type="submit"
          desabilitado={!alterado || gravar.isPending}
          motivo={
            gravar.isPending
              ? 'Conferindo as operações no serviço…'
              : conflito
                ? 'Outra pessoa salvou antes: use a versão salva.'
                : 'Nada mudou.'
          }
        >
          {gravar.isPending ? 'Salvando…' : 'Salvar agenda'}
        </Botao>
      </div>
    </form>
  );
}

const outraPessoaSalvou = (erro: unknown): boolean =>
  erro instanceof ErroDaApi && erro.codigo === 'CONFLITO_DE_VERSAO';

/**
 * O que a parte recebe, numa linha. Com muitos campos (o agendar leva vinte e
 * um), a lista inteira empurrava a tela: a operação declara pelo nome só os que
 * a clínica grava, e a mensagem do servidor diz o que falta.
 */
function recebe(parametros: readonly string[]): string {
  if (parametros.length === 0) return 'Não recebe parâmetros.';
  if (parametros.length <= 6) return `Recebe: ${parametros.join(', ')}.`;
  return `Recebe, pelo nome, os campos que a clínica grava, entre os ${parametros.length} da parte (${parametros.slice(0, 3).join(', ')}…).`;
}
